'use client'

import { convertAddressStringToObject } from '@repo/utils/addresses'
import { CustomDataFieldAddress } from '@repo/custom-data-pages/CustomDataFieldAddress'
import { CustomDataSaveButtons } from '@repo/custom-data-pages/CustomDataSaveButtons'
import { DateSelection } from '@repo/ui/DateSelection'
import { ExpertTypeSelect } from '@repo/ui/ExpertTypeSelect'
import { MarketingIntroductionSourcesSelect } from '@repo/ui/MarketingIntroductionSourcesSelect'
import { MarketingMailingCategoriesSelect } from '@repo/ui/MarketingMailingCategoriesSelect'
import { TextEntry } from '@repo/ui/TextEntry'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useState, useEffect } from 'react'
import { ValidationErrorAlert } from '@repo/ui/ValidationErrorAlert'
import MUICheckbox from '@mui/material/Checkbox'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const OrganisationsAddEditForm = ({ organisationId, onSuccess, onCancel, testData }) => {
  const {
    organisationsById,
    createOrganisation,
    expertTypesById,
    fetchOrganisationById,
    updateOrganisation,
    checkIfOrganisationIdExists
  } = useLookupsStore()

  const [formData, setFormData] = useState({
    organisationId: null,
    name: null,
    email: null,
    phoneNo: null,
    dxNo: null,
    faxNo: null,
    house: '',
    area: '',
    postalTown: '',
    county: '',
    postcode: '',
    websiteUrl: null,
    notes: null,
    expertTypeId: null,
    marketingMailingCategoryId: null,
    marketingIntroductionSourceId: null,
    hasOptedInToEmail: false,
    optedInToEmailDateTime: new Date(),
    optedInToEmailSourceUrl: null
  })

  const [address, setAddress] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [validationMessage, setValidationMessage] = useState(null)
  const organisationIdCharacterLimit = 30

  // Load existing organisation data if editing
  useEffect(() => {
    if (organisationId && organisationsById[organisationId]) {
      const organisation = organisationsById[organisationId]
      setFormData({
        organisationId: organisation.organisationId || null,
        name: organisation.name || null,
        email: organisation.email || null,
        phoneNo: organisation.phoneNo || null,
        dxNo: organisation.dxNo || null,
        faxNo: organisation.faxNo || null,
        house: organisation.house || '',
        area: organisation.area || '',
        postalTown: organisation.postalTown || '',
        county: organisation.county || '',
        postcode: organisation.postcode || '',
        websiteUrl: organisation.websiteUrl || null,
        notes: organisation.notes || null,
        expertTypeId: organisation.expertTypeId || null,
        marketingMailingCategoryId: organisation.marketingMailingCategoryId || null,
        marketingIntroductionSourceId: organisation.marketingIntroductionSourceId || null,
        hasOptedInToEmail: organisation.hasOptedInToEmail || false,
        optedInToEmailDateTime: organisation.optedInToEmailDateTime ? new Date(organisation.optedInToEmailDateTime) : new Date(),
        optedInToEmailSourceUrl: organisation.optedInToEmailSourceUrl || null
      })

      const address = [
        organisation.house,
        organisation.area,
        organisation.postalTown,
        organisation.county,
        organisation.postcode
      ].join('|')
      setAddress(address)
    }
  }, [organisationId, organisationsById, expertTypesById])

  useEffect(() => {
    // generate organisationId, but only when in create mode
    if (organisationId) return
    const generatedOrganisationId = (formData?.name?.replace(/\s+/g, '').toUpperCase() || '').slice(
      0,
      organisationIdCharacterLimit
    )
    handleInputChange('organisationId', generatedOrganisationId)
  }, [formData.name, organisationId])

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const handleAddressChange = (value) => {
    const address = convertAddressStringToObject(value)

    setFormData((prev) => ({
      ...prev,
      ...address
    }))
  }

  const validateForm = async () => {
    const newErrors = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.organisationId?.trim()) {
      newErrors.organisationId = 'ID is required'
    }

    if (!organisationId && formData.organisationId) {
      const exists = await checkIfOrganisationIdExists({ id: formData.organisationId })

      if (exists) {
        newErrors.organisationId = 'ID already exists'
      }
    }

    if (formData?.email && (!formData?.email?.includes('@') || !formData?.email?.includes('.'))) {
      newErrors.email = 'Please enter a valid email'
    }

    setErrors(newErrors)
    if (Object.entries(newErrors).length) console.log(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const isValid = await validateForm()

    if (!isValid) {
      setValidationMessage(null)
      return
    }

    setIsLoading(true)

    try {
      if (organisationId) {
        // Update existing organisation
        await updateOrganisation({ organisationId, ...formData })

        if (onSuccess) onSuccess({ organisationId })
      } else {
        // Create new organisation
        const newOrganisation = await createOrganisation(formData)
        await fetchOrganisationById({ id: newOrganisation.organisationId })
        if (onSuccess) onSuccess({ organisationId: newOrganisation.organisationId })
      }
    } catch (error) {
      console.error('Error saving organisation:', error)
      setErrors({ submit: 'Failed to save organisation. Please try again.' })

      const { errors } = error?.response?.data || {}
      const message = errors ? Object.values(errors).join('\n') : null
      setValidationMessage(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} data-testid="OrganisationsAddEditForm">
      <MUIStack
        direction="column"
        gap={2}
        sx={{
          width: '100%',
          backgroundColor: '#DFD1B655',
          padding: '20px 20px 30px',
          borderRadius: '6px',
          position: 'relative'
        }}
      >
        <MUIStack direction="row" justifyContent="flex-end" gap={2}>
          <CustomDataSaveButtons
            onSave={handleSubmit}
            onCancel={onCancel}
            sx={{ width: '200px', position: organisationId ? 'absolute' : 'static', right: 0, top: '-65px' }}
            disabled={isLoading}
            type="submit"
          />
        </MUIStack>

        <ValidationErrorAlert errorCount={Object.keys(errors).length} isTop validationMessage={validationMessage} />

        <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 2 }}>
          Name & ID
        </MUITypography>
        <MUIStack direction="row" gap={2} flexWrap="wrap">
          <TextEntry
            name="name"
            label="Name"
            value={formData.name}
            onChange={(value) => handleInputChange('name', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={255}
            required
            errors={errors}
          />

          <TextEntry
            name="organisationId"
            label="ID"
            value={formData.organisationId}
            onChange={(value) => handleInputChange('organisationId', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={organisationIdCharacterLimit}
            required
            errors={errors}
            readOnly={Boolean(organisationId)}
          />
        </MUIStack>

        <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 2, mt: 8 }}>
          Type
        </MUITypography>
        <MUIStack direction="row" gap={2} flexWrap="wrap">
          <ExpertTypeSelect
            value={expertTypesById[formData.expertTypeId] || null}
            testData={testData}
            sx={{ minWidth: '240px', flex: 1 }}
            onChange={(value) => {
              handleInputChange('expertTypeId', value?.expertTypeId || null)
            }}
            errors={errors}
          />
        </MUIStack>

        <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 2, mt: 8 }}>
          Contact details
        </MUITypography>
        <MUIStack direction="row" gap={2} flexWrap="wrap">
          <TextEntry
            name="email"
            label="Email"
            value={formData.email}
            onChange={(value) => handleInputChange('email', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={100}
            type="email"
            errors={errors}
          />
          <TextEntry
            name="phoneNo"
            label="Phone"
            value={formData.phoneNo}
            onChange={(value) => handleInputChange('phoneNo', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={20}
            errors={errors}
          />
        </MUIStack>
        <MUIStack direction="row" gap={2} flexWrap="wrap">
          <TextEntry
            name="dxNo"
            label="DX"
            value={formData.dxNo}
            onChange={(value) => handleInputChange('dxNo', value)}
            size="small"
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            limit={20}
            errors={errors}
          />
          <TextEntry
            name="faxNo"
            label="Fax"
            value={formData.faxNo}
            onChange={(value) => handleInputChange('faxNo', value)}
            sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
            size="small"
            limit={20}
            errors={errors}
          />
        </MUIStack>
        <TextEntry
          name="websiteUrl"
          label="URL"
          value={formData.websiteUrl}
          onChange={(value) => handleInputChange('websiteUrl', value)}
          size="small"
          sx={{ backgroundColor: 'white', minWidth: '240px' }}
          limit={100}
          errors={errors}
        />

        <CustomDataFieldAddress value={address} onChange={(value) => handleAddressChange(value)} sx={{ mt: 8 }} />

        <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 2, mt: 8 }}>
          Notes
        </MUITypography>
        <TextEntry
          name="notes"
          label={null}
          value={formData.notes}
          onChange={(value) => handleInputChange('notes', value)}
          sx={{ backgroundColor: 'white', minWidth: '240px' }}
          size="small"
          multiline
          rows={12}
          errors={errors}
        />

        <MUITypography variant="h6" sx={{ textTransform: 'uppercase', mb: 2, mt: 8 }}>
          Marketing Details
        </MUITypography>
        <MUIStack direction="row" gap={2} flexWrap="wrap">
          <MarketingMailingCategoriesSelect
            value={formData.marketingMailingCategoryId}
            sx={{ minWidth: '240px', flex: 1 }}
            testData={testData}
            onChange={(value) => {
              handleInputChange('marketingMailingCategoryId', value?.marketingMailingCategoryId || null)
            }}
            errors={errors}
          />
          <MarketingIntroductionSourcesSelect
            value={formData.marketingIntroductionSourceId}
            sx={{ minWidth: '240px', flex: 1 }}
            testData={testData}
            onChange={(value) => {
              handleInputChange('marketingIntroductionSourceId', value?.marketingIntroductionSourceId || null)
            }}
            errors={errors}
          />
        </MUIStack>
        <MUIFormControlLabel
          control={
            <MUICheckbox
              checked={formData.hasOptedInToEmail}
              onChange={(e) => handleInputChange('hasOptedInToEmail', e.target.checked)}
            />
          }
          label="Opted in to email"
        />
        {formData.hasOptedInToEmail ? (
          <MUIStack direction="row" gap={2} flexWrap="wrap">
            <DateSelection
              label="Opted in to email date"
              value={formData.optedInToEmailDateTime}
              onChange={(value) => handleInputChange('optedInToEmailDateTime', value)}
              size="small"
              name="optedInToEmailDateTime"
              errors={errors}
              sx={{ flex: 1, minWidth: '240px' }}
            />
            <TextEntry
              name="optedInToEmailSourceUrl"
              label="Opted in to email source URL"
              value={formData.optedInToEmailSourceUrl}
              onChange={(value) => handleInputChange('optedInToEmailSourceUrl', value)}
              size="small"
              sx={{ flex: 1, backgroundColor: 'white', minWidth: '240px' }}
              errors={errors}
            />
          </MUIStack>
        ) : null}

        <ValidationErrorAlert errorCount={Object.keys(errors).length} validationMessage={validationMessage} />

        <MUIStack direction="row" justifyContent="flex-end" gap={2} sx={{ mt: 4 }}>
          <CustomDataSaveButtons
            onSave={handleSubmit}
            onCancel={onCancel}
            sx={{ width: '200px' }}
            disabled={isLoading}
            type="submit"
          />
        </MUIStack>
      </MUIStack>
    </form>
  )
}

OrganisationsAddEditForm.propTypes = {
  organisationId: PropTypes.any,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  testData: PropTypes.array
}
